import dayjs from 'dayjs';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TaskFormField } from 'bom/elements';
import { useLogActions } from 'bom/hooks';
import { ILog } from 'bom/types';
import { Form, FormField } from 'core/components';
import { DB_DATE_FORMAT } from 'core/constants';
import { InputType } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { formatDateForServer } from 'core/utils';
import { CorporateUserFormField } from 'corporate/elements';

import { RemoveLogsButton } from './RemoveLogsButton';
import { upsertLogSchema } from './schema';
import { IUpsertLogFormData } from './types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  log: ILog;
  onSubmitSuccess?: (log: ILog) => void;
}

export const UpsertLog = ({ isOpen, onClose, log, onSubmitSuccess }: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();

  const { upsertLog } = useLogActions();

  const isEdit = useMemo(() => !!log?.id, [log?.id]);

  const defaultValues = useMemo(() => {
    if (!isEdit) {
      return null;
    }

    return {
      user: log.userId
        ? {
            id: log.userId,
            name: log.userName,
          }
        : null,
      note: log.note,
      task: log.bomTaskId
        ? {
            id: log.bomTaskId,
            name: log.description,
          }
        : null,
      activityDate: log.activityDate ? dayjs(log.activityDate) : null,
      duration: log.duration,
    };
  }, [isEdit, log]);

  const onSubmit =
    (reset: UseFormReset<IUpsertLogFormData>) => async (data: IUpsertLogFormData) => {
      const idPayload = isEdit ? { id: log.id } : {};

      const { success, bomLog: updatedLog } = await upsertLog({
        bomLog: {
          ...idPayload,
          activityDate: formatDateForServer(data.activityDate, DB_DATE_FORMAT),
          duration: data.duration,
          note: data.note,
          bomTaskId: data.task?.id,
          userId: data.user?.userId,
        },
      });

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);
        onSubmitSuccess?.(updatedLog);
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<IUpsertLogFormData>) => {
    reset?.(defaultValues);
    onClose();

    clearNotification();
  };

  const extraButtons = useMemo(() => {
    return isEdit ? [<RemoveLogsButton key="removeLogsButton" logs={[log]} />] : [];
  }, [isEdit, log]);

  return (
    <FormProvider schema={upsertLogSchema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        title={isEdit ? t('titleUpdateLog') : t('titleAddLog')}
        submitBtnLabel={isEdit ? t('buttonLabelUpdateLog') : t('buttonLabelAddLog')}
        extraButtons={extraButtons}
      >
        <CorporateUserFormField name="user" required />

        <TaskFormField name="task" required />

        <FormField
          name="activityDate"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelActivityDate'),
            required: true,
          }}
        />

        <FormField
          name="duration"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            label: t('labelDurationHours'),
            required: true,
            slotProps: {
              input: {
                min: 0,
                max: 24,
                thousandSeparator: true,
                decimalScale: 2,
                suffix: ` ${t('labelHrs')}`,
              },
            },
          }}
        />

        <FormField
          name="note"
          type={InputType.TEXTAREA}
          inputProps={{
            label: t('labelNote'),
            placeholder: t('placeholderNote'),
          }}
        />
      </Form>
    </FormProvider>
  );
};
